import { NO_DRAFT } from 'entities/draft';
import { requireValue } from 'shared/lib/assert';
import type { DraftStyleEntry } from 'entities/document';

interface DraftSourceDocument {
  data?:
    | {
        superId: number | string;
        revisionId: string;
        organization: unknown;
        [key: string]: unknown;
      }
    | undefined;
}

type Draft = {
  documentSuperId: number | string;
  documentRevisionId: string;
  organization: unknown;
  content: DraftStyleEntry[];
};

const templateSuperIdOf = (entry: DraftStyleEntry): unknown => ('templateSuperId' in entry ? entry.templateSuperId : undefined);

function entryMatcher(entry: DraftStyleEntry): (e: DraftStyleEntry) => boolean {
  return (e) => {
    switch (entry.type) {
      case 'graphical-variables':
        return e.type === 'graphical-variables';
      case 'document':
        return e.type === 'document';
      case 'form-classes':
        return e.type === 'form-classes';
      case 'builtin-css':
        return e.type === 'builtin-css';
      default:
        return templateSuperIdOf(entry) === templateSuperIdOf(e);
    }
  };
}

function insertIndex(draft: Draft, entry: DraftStyleEntry): number {
  switch (entry.type) {
    case 'graphical-variables':
      return 0;
    case 'form-classes':
      return draft.content.findIndex((c) => c.type === 'graphical-variables') + 1;
    case 'builtin-css':
      return draft.content.length;
    case 'document':
      return draft.content.findIndex((c) => c.type === 'form-classes' || c.type === 'graphical-variables') + 1;
    default:
      return draft.content.length;
  }
}

export function createNewDraft(pDocument: DraftSourceDocument, draft: Draft | typeof NO_DRAFT, entry: DraftStyleEntry): Draft {
  const source = requireValue(pDocument.data, 'createNewDraft needs a loaded document');
  const emptyDraft: Draft = {
    documentSuperId: source.superId,
    documentRevisionId: source.revisionId,
    organization: source.organization,
    content: [],
  };

  const d: Draft = window.structuredClone(draft === NO_DRAFT ? emptyDraft : draft);
  const i = d.content.findIndex(entryMatcher(entry));
  if (i === -1) {
    d.content.splice(insertIndex(d, entry), 0, entry);
  } else {
    d.content.splice(i, 1, entry);
  }
  return d;
}
